import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

import { BaseProductFragment } from '../../../generated/graphql.generated';
import { COLOR_MAP, isColorName } from '../../colors';
import { 
  EventAttendeeRoles, 
  eventTypeInfoMap, 
  JOB_ROLE_MAP, 
  EventLocationTypes
} from '../../global.constants';
import { strToTitleCase } from '../../js';
import { FreyaHelperService } from '../../services/freya-helper.service';
import { getLocationArrivalAndDepartureTimes } from '../../shared/event-location/calendarevent.util';
import { PRODUCT_LABOR_CATEGORY } from '../../shared/product-categories/product-categories';
import { ensureUnixSeconds } from '../../time';
import { formatUserName, lastNameFirstName } from '../../users/users.utils';
import { extractPostalCodeFromAddressLineOne, getFirstLocation, getLocationAreaCode } from '../../utilities/locations.util';
import { CrewListComponent } from '../crew-list/crew-list.component';
import { ExtendedCalendarEventForScheduleFragment, EventColorInfo } from '../schedule.interfaces';
import { EVENT_STATUS_ICON_MAP, showWelcomeCallIndicator } from '../schedule.util';

// Define a type for Crew Member to avoid 'any'
export interface CrewMember {
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
    imageUrl?: string;
  };
  role: string;
  isDriver?: boolean;
}

@Component({
  selector: 'app-schedule-event',
  templateUrl: './schedule-event.component.html',
  styleUrls: ['./schedule-event.component.scss'],
  standalone: true,
  imports: [CommonModule, CrewListComponent]
})
export class ScheduleEventComponent implements OnInit, OnChanges {
  @Input() event: ExtendedCalendarEventForScheduleFragment;
  
  private _viewType: string;
  @Input() set viewType(value: string) {
    this._viewType = value;
  }
  get viewType(): string {
    return this._viewType;
  }

  @Input() showCrew = false;
  @Input() hidden = false;
  @Input() editable = false;
  @Input() hasBeenEdited = false;
  @Input() disabled = false;
  @Input() paymentBorderColor = 'transparent';
  
  // These were inputs, now calculated internally (or passed if needed, but we refactor to internal)
  // We keep them as properties for template binding
  colors: EventColorInfo;
  title: string;
  startPostalCode: string;
  endPostalCode: string;
  timelineSegments: { type: string, width: number, color: string, title: string }[] = [];
  statusIcon: string;
  showWelcomeCall = false;
  eventTotal: string;
  private _crewSize: number;
  @Input() set crewSize(value: number) {
    this._crewSize = value;
  }
  get crewSize(): number {
    return this._crewSize;
  }
  totalTime: string;
  totalDriveTime: string;
  
  // Inputs passed from FullCalendarHelperService createFreyaEvent wrapper
  @Input() commonCrew: any[] = []; 
  @Input() skipPaymentBorder = false;

  crewAttendees: CrewMember[] = [];

  get isMonthView(): boolean {
    return this.viewType === 'dayGridMonth';
  }

  constructor(
    private freyaHelper: FreyaHelperService
  ) {}

  ngOnInit() {
    this.calculateEventData();
  }

  // Requirement: Respond to input changes for event or paymentBorderColor updates
  // This ensures the component re-renders correctly when parent data changes without full recreation
  ngOnChanges(changes: SimpleChanges) {
    if (changes.event || changes.skipPaymentBorder) {
      this.calculateEventData();
    }
  }

  // Recalculate if event changes (though usually component is recreated)
  calculateEventData() {
    if (!this.event) return;

    // Reset postal codes to prevent stale values
    this.startPostalCode = '';
    this.endPostalCode = '';

    this.colors = this.determineColorsFromEvent(this.event);
    this.timelineSegments = this.calculateTimelineSegments(this.event, this.colors);
    this.paymentBorderColor = this.skipPaymentBorder ? 'transparent' : this.getPaymentStatusBorderColor(this.event);
    this.statusIcon = this.determineIconFromEvent(this.event);
    this.showWelcomeCall = showWelcomeCallIndicator(this.event);
    this.title = this.resolveTitle(this.event);

    // Locations
    const loc = getFirstLocation(this.event);
    if (loc?.location) {
      this.startPostalCode = loc.location?.areaCode
      ? getLocationAreaCode(loc.location)
      : extractPostalCodeFromAddressLineOne(loc.location.addressLineOne)
    } else if (!this.event?.eventIsPlaceholder) {
      this.startPostalCode = 'No Area';
    }

    const endLocation = getFirstLocation(this.event, EventLocationTypes.end, true);
    if (endLocation?.location) {
      this.endPostalCode = getLocationAreaCode(endLocation.location);
    }

    // Stats
    this.eventTotal = this.event.discountedSubTotal ? `$${(this.event.discountedSubTotal / 100).toFixed(2)}` : '$0.00';
    
    // Time
    const eventStart = ensureUnixSeconds(this.event.start);
    const eventEnd = ensureUnixSeconds(this.event.end);
    const eventDuration = eventEnd - eventStart;
    this.totalTime = `${(eventDuration / 3600).toFixed(1).replace('.0', '')}h`;

    // Drive Time
    let totalDriveSeconds = 0;
    let currentT = eventStart;
    const locationsWithTimes = getLocationArrivalAndDepartureTimes(this.event);
    const sortedLocs = [...(locationsWithTimes || [])].sort((a, b) => a.arrivalTime - b.arrivalTime);
    
    for (const loc of sortedLocs) {
       if (loc.arrivalTime > currentT) {
          totalDriveSeconds += (loc.arrivalTime - currentT);
       }
       const departure = Math.min(loc.departureTime, eventEnd);
       currentT = Math.max(currentT, departure);
    }
    if (currentT < eventEnd) {
       totalDriveSeconds += (eventEnd - currentT);
    }
    this.totalDriveTime = `${(totalDriveSeconds / 3600).toFixed(1).replace('.0', '')}h`;

    // Crew
    // Only calculate if crewSize not passed? But we use the setter now.
    // If not passed (undefined), calculate it.
    if (this.crewSize === undefined) {
        // Try to find a labor product explicitly first
        let laborProduct: Pick<BaseProductFragment, 'id' | 'name' | 'category' | 'metadata'> = this.event.charges?.map(c => c.product)
             .find(p => p?.category === PRODUCT_LABOR_CATEGORY);
        
        if (!laborProduct) {
             laborProduct = this.colors.leftDivColorProduct;
        }

        const inferred = this.determineCrewSizeFromProduct(laborProduct);
        if (inferred !== null) {
          this.crewSize = inferred;
        }
    }
    this.crewAttendees = this.extractCrewAttendees(this.event);
  }

  get sortedCrewAttendees(): CrewMember[] {
    const mergedMap = new Map<string, CrewMember>();

    // Common Crew
    this.commonCrew?.forEach(c => {
      if (c.user?.id) mergedMap.set(c.user.id, c);
    });

    // Event Attendees
    this.crewAttendees?.forEach(c => {
      if (c.user?.id) {
        const existing = mergedMap.get(c.user.id);
        // If existing is lead and incoming is not, keep existing to preserve lead role
        if (existing && this.isLead(existing) && !this.isLead(c)) {
          return;
        }
        mergedMap.set(c.user.id, c);
      }
    });

    const unique = Array.from(mergedMap.values());

    return unique.sort((a, b) => {
      const aLead = this.isLead(a);
      const bLead = this.isLead(b);
      if (aLead && !bLead) return -1;
      if (!aLead && bLead) return 1;
      return 0;
    });
  }

  get crewSummary(): string {
    // If Vertical View, show names. If Horizontal (Timeline), show stats.
    if (this.isVerticalView) {
      if (!this.sortedCrewAttendees || !this.sortedCrewAttendees.length) {
        return '';
      }

      const parts: string[] = [];
      const lead = this.sortedCrewAttendees.find(c => this.isLead(c));

      if (lead) {
        parts.push(formatUserName(lead.user as any));
      }

      const others = this.sortedCrewAttendees.filter(c => c !== lead);
      others.forEach(c => {
        parts.push(formatUserName(c.user as any));
      });

      return parts.join(' + ');
    } else {
      // Horizontal View: Show Stats
      const assigned = this.sortedCrewAttendees ? this.sortedCrewAttendees.length : 0;
      const required = this.crewSize || 0;

      if (assigned === 0 && required === 0) {
        return '';
      }

      if (required > 0) {
        return `${assigned}/${required} Assigned`;
      } else {
        return `${assigned} Assigned`;
      }
    }
  }

  private extractCrewAttendees(calendarEvent: any): CrewMember[] {
    let crewAttendees = calendarEvent.crew;

    if (!crewAttendees && calendarEvent.attendees?.length) {
      const crewRoles: string[] = [
        EventAttendeeRoles.crewLead,
        EventAttendeeRoles.assistantCrewLead,
        EventAttendeeRoles.crewMember,
        EventAttendeeRoles.driver
      ];

      const mergedCrewMap = new Map<string, any>();

      for (const attendee of calendarEvent.attendees) {
        if (crewRoles.includes(attendee.role) && attendee.user?.id) {
          const existing = mergedCrewMap.get(attendee.user.id);
          const isDriver = attendee.role === EventAttendeeRoles.driver || (attendee as any).isDriver;

          if (!existing) {
            mergedCrewMap.set(attendee.user.id, { ...attendee, isDriver });
          } else {
            if (isDriver) {
              existing.isDriver = true;
            }
            if (attendee.role === EventAttendeeRoles.crewLead || attendee.role === EventAttendeeRoles.assistantCrewLead) {
              if (existing.role !== EventAttendeeRoles.crewLead && existing.role !== EventAttendeeRoles.assistantCrewLead) {
                existing.role = attendee.role;
              }
            }
          }
        }
      }
      crewAttendees = Array.from(mergedCrewMap.values());
    }
    return crewAttendees || [];
  }

  // --- Helper Logic moved from Service ---

  resolveTitle(calendarEvent: ExtendedCalendarEventForScheduleFragment) {
    const customer = calendarEvent?.job?.users?.find((a) => a.role === JOB_ROLE_MAP.customerRole);
    if (!customer) {
      return calendarEvent.title;
    }
    return customer.user.company?.trim() || lastNameFirstName(customer.user);
  }

  determineColorsFromEvent(ce?: ExtendedCalendarEventForScheduleFragment): EventColorInfo {
    let eventColorInfo: EventColorInfo = {
      backgroundColor: COLOR_MAP.blue.color,
      textColor: COLOR_MAP.blue.textColor,
      travelTimeColor: COLOR_MAP.blue.lightColor,
    };
    if (!ce) {
      return eventColorInfo;
    }

    const eventTypeInfo = eventTypeInfoMap[ce.type];
    const eventBackgroundColour = ce?.colour || eventTypeInfoMap[ce.type]?.backgroundColor;
    const eventTextColour = ce?.colour || eventTypeInfoMap[ce.type]?.textColor;
    if (eventTypeInfo && (isColorName(eventBackgroundColour))) {
      const color = COLOR_MAP[eventBackgroundColour];
      eventColorInfo.backgroundColor = color.color;
      eventColorInfo.textColor = color.textColor || '#000000';
      eventColorInfo.travelTimeColor = color.lightColor;
    } else if (eventTypeInfo?.backgroundColor) {
      eventColorInfo.backgroundColor = eventBackgroundColour;
      eventColorInfo.textColor = eventTextColour || '#000000';
    }

    const products = ce.charges?.map((c) => c.product) || [];
    for (const product of products) {
      const productColorInfo = this.determineColorInfoFromProduct(product);
      if (productColorInfo) {
        eventColorInfo = {
          ...eventColorInfo,
          ...productColorInfo,
        };
        break;
      }
    }

    // Zones
    const startLocation = ce.locations?.find((l) => l.type === 'start');
    if (startLocation?.location?.serviceArea) {
      eventColorInfo.zoneBackgroundColor = startLocation?.location?.serviceArea?.color;
      eventColorInfo.zoneTextColor = '#000000';
    } else if (ce.zones?.nodes?.length) {
      const firstZoneWithcolor = ce.zones?.nodes?.find((z) => z.color);
      if (firstZoneWithcolor) {
        eventColorInfo.zoneBackgroundColor = firstZoneWithcolor.color;
        eventColorInfo.zoneTextColor = '#000000';
      } else {
        eventColorInfo.zoneBackgroundColor = 'black';
        eventColorInfo.zoneTextColor = 'white';
      }
    } else {
      eventColorInfo.zoneBackgroundColor = 'black';
    }

    const endLocation = ce.locations?.find((l) => l.type === 'end');
    if(!endLocation?.location?.serviceArea) {
      eventColorInfo.endAreaColor = eventColorInfo.zoneBackgroundColor;
    } else if (endLocation?.location?.serviceArea){
      eventColorInfo.endAreaColor = endLocation?.location?.serviceArea?.color;
    } else {
      eventColorInfo.endAreaColor = 'black';
    }

    return eventColorInfo;
  }

  determineColorInfoFromProduct(product: Pick<BaseProductFragment, 'id' | 'name' | 'category' | 'metadata'>) {
    if (!product) { return undefined; }
    if (product?.category !== PRODUCT_LABOR_CATEGORY) { return; }
    const metaColor = product?.metadata?.color;
    if (isColorName(metaColor) && COLOR_MAP[metaColor]) {
      const color = COLOR_MAP[metaColor];
      return {
        leftDivColor: color?.color,
        leftDivTextColor: color?.textColor,
        leftDivColorProduct: product,
      };
    }
    return undefined;
  }

  determineIconFromEvent(event: ExtendedCalendarEventForScheduleFragment): string{
    const icon = EVENT_STATUS_ICON_MAP.find((esi) => esi?.status === event?.status);
    if (!icon && !event?.eventIsPlaceholder){
      return 'pi-bars';
    }
    if (event?.eventIsPlaceholder) {
      return 'pi-sync';
    }
    return icon?.icon;
  }

  getPaymentStatusBorderColor(calendarEvent: ExtendedCalendarEventForScheduleFragment): string {
    if (calendarEvent.type === 'estimating' || calendarEvent.type === 'virtualEstimate') {
      return 'transparent';
    }
    if (!calendarEvent?.job) {
      return '#FF8C00';
    }
    const job = calendarEvent.job;
    const totalAmount = job.total || 0;
    const paidAmount = job.paidTotal || 0;
    if (paidAmount >= totalAmount && totalAmount > 0) {
      return '#00FF00';
    }
    if (paidAmount > 0 && paidAmount < totalAmount) {
      return '#FFFF00';
    }
    return '#DC143C';
  }

  private determineCrewSizeFromProduct(product: any): number | null {
    if (!product) return null;
    
    // Updated logic per feedback: extract first number from name if category is Labor
    // Note: product category check is done before calling this usually, but checking here is safe
    
    // Check regex on name
    const match = product.name?.match(/(\d+)/);
    if (match) {
        return parseInt(match[1], 10);
    }
    
    // Fallback to metadata
    if (product.metadata?.crewSize) {
        return parseInt(product.metadata.crewSize, 10);
    }
    
    return null;
  }

  calculateTimelineSegments(calendarEvent: ExtendedCalendarEventForScheduleFragment, colors: EventColorInfo) {
    const eventStart = ensureUnixSeconds(calendarEvent.start);
    const eventEnd = ensureUnixSeconds(calendarEvent.end);
    const totalDuration = eventEnd - eventStart;
    
    if (totalDuration <= 0) return [];

    const segments = [];
    let currentTime = eventStart;

    const locationsWithTimes = getLocationArrivalAndDepartureTimes(calendarEvent);
    const sortedLocations = [...(locationsWithTimes || [])]
      .sort((a, b) => a.arrivalTime - b.arrivalTime);

    for (const loc of sortedLocations) {
      const zoneColor = (loc.location as any)?.serviceArea?.color || colors.zoneBackgroundColor || '#000000';
      if (loc.arrivalTime > currentTime) {
        const travelDuration = loc.arrivalTime - currentTime;
        const width = (travelDuration / totalDuration) * 100;
        if (width > 0) {
          segments.push({ 
            type: 'travel', width, color: zoneColor, opacity: 0.5, 
            title: `Travel: ${(travelDuration / 60).toFixed(0)} min` 
          });
        }
      }
      const departure = Math.min(loc.departureTime, eventEnd);
      if (departure > loc.arrivalTime) {
        const workDuration = departure - loc.arrivalTime;
        const width = (workDuration / totalDuration) * 100;
        if (width > 0) {
          segments.push({ 
            type: 'work', width, color: zoneColor, opacity: 1, 
            title: `Work: ${(workDuration / 60).toFixed(0)} min` 
          });
        }
      }
      currentTime = Math.max(currentTime, departure);
    }

    if (currentTime < eventEnd) {
      const lastLoc = sortedLocations[sortedLocations.length - 1];
      const zoneColor = (lastLoc?.location as any)?.serviceArea?.color || colors.endAreaColor || '#000000';
      const remainingDuration = eventEnd - currentTime;
      const width = (remainingDuration / totalDuration) * 100;
      if (width > 0) {
        segments.push({ 
          type: 'travel', width, color: zoneColor, opacity: 0.5, 
          title: `Travel: ${(remainingDuration / 60).toFixed(0)} min` 
        });
      }
    }
    return segments;
  }

  get isVerticalView() {
    return this.viewType?.toLowerCase().includes('timegrid');
  }

  get assetNames() {
    if (!this.event?.assets) return '';
    return this.event.assets.map(a => a.name).join(', ');
  }

  get headerTitle() {
    const parts = [];
    if (this.assetNames) parts.push(this.assetNames);
    const type = strToTitleCase(this.event?.type || '');
    if (type) parts.push(type);
    if (this.event?.title && this.event.title.toLowerCase() !== type.toLowerCase()) {
        parts.push(this.event.title);
    }
    return parts.join(' - ');
  }

  get eventStyle() {
    if (!this.colors) return {};
    return {
      backgroundColor: this.colors.backgroundColor,
      color: this.colors.textColor,
      borderLeft: (this.paymentBorderColor && this.paymentBorderColor !== 'transparent') ? `6px solid ${this.paymentBorderColor}` : 'none'
    };
  }

  isLead(attendee: CrewMember) {
    return attendee.role === EventAttendeeRoles.crewLead || attendee.role === EventAttendeeRoles.assistantCrewLead;
  }
}
